import Divider from 'LuxKit/Divider'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'
import Group from 'components/utils/Group'
import { useAppSelector } from 'hooks/reduxHooks'
import { clamp } from 'lib/maths/mathUtils'
import LoyaltyAccountTileCircularProgressBar from 'loyalty/components/LoyaltyAccountTileCircularProgressBar'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import React, { useMemo } from 'react'
import styled from 'styled-components'
import FormatLoyaltyStatusCredits from './Formatters/FormatLoyaltyStatusCredits'

const TierDivider = styled(Divider)`
  opacity: 0.5;

  &.tier-bronze {
    background-color: ${props => props.theme.palette.product.loyalty.bronze.normalContrast};
  }

  &.tier-silver {
    background-color: ${props => props.theme.palette.product.loyalty.silver.normalContrast};
  }

  &.tier-gold {
    background-color: ${props => props.theme.palette.product.loyalty.gold.normalContrast};
  }

  &.tier-platinum {
    background-color: ${props => props.theme.palette.product.loyalty.platinum.normalContrast};
  }
`

function LoyaltyAccountStatusSummary() {
  const account = useAppSelector(state => state.loyalty.account.data)
  const [tier, tierName, nextTier, nextTierName] = useCurrentLoyaltyTier()

  const statusCredits = account?.statusCredits ?? 0
  const totalCreditsForTierRetention = account?.totalCreditsForTierRetention ?? 0
  const totalCreditsForTierUpgrade = account?.totalCreditsForTierUpgrade ?? 0
  const nextTierCreditsRequired = tier === 'platinum' ? totalCreditsForTierRetention : totalCreditsForTierUpgrade

  const progress = useMemo(() => {
    const baseProgress = Math.floor((statusCredits / (tier === 'platinum' ? totalCreditsForTierRetention : nextTierCreditsRequired)) * 100)
    return clamp(0, baseProgress, 100)
  }, [nextTierCreditsRequired, statusCredits, tier, totalCreditsForTierRetention])

  return <Group
    direction="horizontal"
    verticalAlign="center"
    horizontalAlign="center"
    gap={20}
  >
    <LoyaltyAccountTileCircularProgressBar
      progress={progress}
      tier={tier}
    >
      <Group direction="vertical" horizontalAlign="center" gap={2}>
        <BodyText variant="medium" weight="bold">
          <FormatDecimal value={statusCredits} />
        </BodyText>
        <TierDivider className={`tier-${tier}`} kind="primary" />
        <Caption variant="small">
          <FormatDecimal value={nextTierCreditsRequired} />
        </Caption>
      </Group>
    </LoyaltyAccountTileCircularProgressBar>
    <BodyText variant="small" align="start">
      <b><FormatLoyaltyStatusCredits statusCredits={Math.max(nextTierCreditsRequired - statusCredits, 0)} /></b><br />
      to {nextTier ? `unlock ${nextTierName}` : `retain ${tierName}` }
    </BodyText>
  </Group>
}

export default LoyaltyAccountStatusSummary
